// The `i` disclosure that keeps on-screen helper text short (UI-LAW §4d).
// Inline copy states what the field IS; anything longer — the mental model,
// the edge cases, the worked example — lives here and links out to the
// product docs.
//
// Built on the existing primitives, never a second popover or markdown
// renderer: `Popover` owns Escape, outside-click dismissal, and the panel
// surface; `Markdown` owns sanitized formatting (short paragraphs, a bullet
// list, inline code, links).
import {
  useEffect,
  useId,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
} from "react";

import { Stack } from "./Layout";
import { Markdown } from "./Markdown";
import { Popover } from "./Popover";
import { Link } from "./Typography";

// Long enough to cross the trigger-to-panel gap at a normal pointer speed,
// short enough that the panel never feels stuck open.
const HOVER_CLOSE_GRACE_MS = 160;

export interface HelpTipProps {
  /** Anchor edge the panel grows from. Use `end` near the right gutter. */
  align?: "start" | "end";
  /** Panel body as markdown. Keep it under ~60 words; link out for more. */
  children: string;
  /**
   * Absolute product-docs URL, always from `docUrl()` in
   * `src/lib/docLinks.ts` — never a hand-written string.
   */
  docHref?: string;
  /** Link text for `docHref`. Name the destination page. */
  docLabel?: string;
  /** Accessible name of the trigger and the panel, e.g. "About sweeps". */
  label: string;
}

export function HelpTip({
  align = "start",
  children,
  docHref,
  docLabel = "Read the docs",
  label,
}: HelpTipProps) {
  const panelId = useId();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const rootRef = useRef<HTMLSpanElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  // A click/tap pins the panel open so it survives pointer-leave — the only
  // way to reach the panel's link, and the whole affordance on touch devices
  // where there is no hover at all.
  const [isPinned, setIsPinned] = useState(false);
  // Pointer-leave closes on a short grace period, never instantly: the panel
  // sits a few px below the trigger, so travelling to it crosses a gap that is
  // over neither element. Closing on that transit made the panel's link
  // unreachable by hover — the reason clicking to pin looked mandatory. The
  // CSS bridge covers the straight path; this covers diagonal ones.
  const closeTimer = useRef<number | null>(null);

  const cancelPendingClose = () => {
    if (closeTimer.current !== null) {
      window.clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };

  // Timers are an external system: drop a pending one if the tip unmounts.
  useEffect(() => cancelPendingClose, []);

  const close = () => {
    cancelPendingClose();
    setIsOpen(false);
    setIsPinned(false);
  };

  const dismiss = () => {
    // Escape may be pressed with focus inside the panel; hand focus back to
    // the trigger rather than dropping it on the document. Popover's own
    // return-focus is off because pointer-leave must never steal focus.
    if (rootRef.current?.contains(document.activeElement)) {
      triggerRef.current?.focus();
    }
    close();
  };

  const onTriggerClick = (event: ReactMouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    if (isPinned) {
      close();
      return;
    }
    setIsOpen(true);
    setIsPinned(true);
  };

  return (
    <span
      className="marker-help-tip"
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) {
          close();
        }
      }}
      onFocus={() => {
        cancelPendingClose();
        setIsOpen(true);
      }}
      onPointerEnter={() => {
        cancelPendingClose();
        setIsOpen(true);
      }}
      onPointerLeave={() => {
        if (isPinned) {
          return;
        }
        cancelPendingClose();
        closeTimer.current = window.setTimeout(() => {
          closeTimer.current = null;
          setIsOpen(false);
        }, HOVER_CLOSE_GRACE_MS);
      }}
      ref={rootRef}
    >
      <button
        aria-controls={panelId}
        aria-expanded={isOpen}
        aria-label={label}
        className="marker-help-tip__trigger"
        onClick={onTriggerClick}
        ref={triggerRef}
        type="button"
      >
        {/* Information, not a question: an `i` reads as "here is context",
            where `?` reads as "you are confused". Decorative — the button's
            accessible name is `label`. */}
        <span aria-hidden="true">i</span>
      </button>
      <Popover
        align={align}
        anchorRef={triggerRef}
        aria-label={label}
        className="marker-help-tip__panel"
        id={panelId}
        isOpen={isOpen}
        onDismiss={dismiss}
        returnFocusOnDismiss={false}
        // A hover-revealed explanation is a parenthetical note, not a dialog:
        // it traps no focus and completes no task.
        role="note"
      >
        <Stack gap={2}>
          <Markdown>{children}</Markdown>
          {docHref !== undefined ? (
            <Link
              className="marker-help-tip__doc"
              href={docHref}
              rel="noreferrer noopener"
              target="_blank"
            >
              {docLabel}
            </Link>
          ) : null}
        </Stack>
      </Popover>
    </span>
  );
}
